import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { TerpecaQuote } from 'src/app/models/quote.model';
import { TerpecaRanking, UnrankedReasonEntry, getUnrankedReasonDescription, getUnrankedReasonEmoji } from 'src/app/models/ranking.model';
import { PairwiseComparison } from 'src/app/models/results.model';
import { TerpecaCategory, TerpecaRoom } from 'src/app/models/room.model';
import { ApplicationStatus } from 'src/app/models/user.model';
import { getNominationCount } from 'src/app/utils/misc.utils';

@Component({
  selector: 'app-analyzedialog',
  standalone: false,
  templateUrl: './analyzedialog.component.html',
  styleUrl: './analyzedialog.component.css'
})
export class AnalyzeDialogComponent {
  dialogRef = inject<MatDialogRef<AnalyzeDialogComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);

  Category = TerpecaCategory;
  Status = ApplicationStatus;

  rankingsForRoom(roomId: string) {
    const rankings: TerpecaRanking[] = [];
    if (this.data.user && this.data.user.isOwner && this.data.rankings) {
      for (const ranking of this.data.rankings) {
        if (ranking.unsortedIds &&
            (!roomId || ranking.unsortedIds.map((id: string) => this.getRemappedId(id)).includes(roomId))) {
          rankings.push(ranking);
        }
      }
    }
    return rankings;
  }

  numNominationsForRoom(room: TerpecaRoom) {
    return getNominationCount(room, this.data.year);
  }

  isIgnoredRoom(ranking: TerpecaRanking, roomId: string) {
    return this.data.ignoredVoterRooms?.get(ranking.userId)?.has(roomId) || false;
  }

  rankedCount(ranking: TerpecaRanking) {
    return ranking.rankedIds?.length || 0;
  }

  rankedCountWithoutIgnored(ranking: TerpecaRanking) {
    return (ranking.rankedIds || []).filter(id => !this.isIgnoredRoom(ranking, id)).length;
  }

  analysisRankLabel(roomId: string) {
    const entity = this.data.entityMap?.get(this.getRemappedId(roomId));
    if (!entity) {
      return '';
    }
    return (entity.unranked ? 'NR' : '#' + entity.rank) + ' ';
  }

  getRemappedId(id: string) {
    return this.data.roomIdRemap.has(id) ? this.data.roomIdRemap.get(id) : id;
  }

  unrankedRoomIds(ranking: TerpecaRanking) {
    return Object.keys(ranking.unrankedReasonMap || {});
  }

  getUnrankedReasonInfo(ranking: TerpecaRanking, roomId: string) {
    if ((ranking.unrankedReasonMap || {})[roomId]) {
      const entry: UnrankedReasonEntry = ranking.unrankedReasonMap[roomId];
      return `${getUnrankedReasonEmoji(entry)} ${getUnrankedReasonDescription(entry)}`;
    }
    return '';
  }

  color(record: PairwiseComparison) {
    if (record.confidence > 0.5) {
      return `rgb(0, 150, 0)`;
    }
    if (record.confidence < 0.5) {
      return `rgb(150, 0, 0)`;
    }
    return 'inherit';
  }

  fontWeight(record: PairwiseComparison) {
    if (record.directComps + record.secondaryComps === 0) {
      return 100;
    }
    return 200 + Math.round(1000 * Math.abs(record.confidence - 0.5));
  }

  hasQuote(userId: string, roomId: string): boolean {
    if (!this.data.quotesMap) {
      return false;
    }
    const key = `${userId}-${roomId}`;
    return this.data.quotesMap.has(key);
  }

  hasVideoInterest(userId: string, roomId: string): boolean {
    if (!this.data.quotesMap) {
      return false;
    }
    const key = `${userId}-${roomId}`;
    const quote: TerpecaQuote = this.data.quotesMap.get(key);
    return quote && quote.videoInterest;
  }

  close() {
    this.dialogRef.close(false);
  }
}
